import httpClient from '../../../common/services/httpClient';
import {handleError} from '../../../common/utils/errorHandling';
import {Image, UpdateImage} from '../types/Image';

export interface ImageUploadResponse {
  id: string;
  width: number;
  height: number;
  size: number;
}

class ImagesService {
  public async uploadImageForPlace(placeId: string, file: File): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return handleError(
      () => httpClient.postFormData<ImageUploadResponse>(`/images/places/${placeId}`, formData),
      'Error uploading image:'
    );
  }

  public async uploadImageForArtisticObject(artisticObjectId: string, file: File): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return handleError(
      () => httpClient.postFormData<ImageUploadResponse>(`/images/artistic-objects/${artisticObjectId}`, formData),
      'Error uploading image:'
    );
  }

  public async deletePlaceImage(placeId: string, imageId: string): Promise<void> {
    return handleError(
      () => httpClient.delete(`/places/${placeId}/images/${imageId}`),
      'Error deleting place image:'
    );
  }

  public async deleteArtisticObjectImage(artisticObjectId: string, imageId: string): Promise<void> {
    return handleError(
      () => httpClient.delete(`/artistic-objects/${artisticObjectId}/images/${imageId}`),
      'Error deleting artistic object image:'
    );
  }

  public async updateImage(imageId: string, updateData: UpdateImage): Promise<Image> {
    return handleError(
      () => httpClient.put<Image>(`/images/${imageId}`, updateData),
      'Error updating image:'
    );
  }

  public async reorderPlaceImages(placeId: string, imageIds: string[]): Promise<void> {
    return handleError(
      () => httpClient.put(`/images/places/${placeId}/reorder`, { imageIds }),
      'Error reordering images:'
    );
  }

  public async reorderArtisticObjectImages(artisticObjectId: string, imageIds: string[]): Promise<void> {
    return handleError(
      () => httpClient.put(`/images/artistic-objects/${artisticObjectId}/reorder`, { imageIds }),
      'Error reordering images:'
    );
  }

  public async uploadImageForTile(tileId: string, file: File): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return handleError(
      () => httpClient.postFormData<ImageUploadResponse>(`/images/tiles/${tileId}`, formData),
      'Error uploading tile image:'
    );
  }

  public async deleteTileImage(tileId: string, imageId: string): Promise<void> {
    return handleError(
      () => httpClient.delete(`/tiles/${tileId}/images/${imageId}`),
      'Error deleting tile image:'
    );
  }

  public async setFocalPoint(imageId: string, focalPoint: { x: number, y: number } | null): Promise<void> {
    return handleError(
      () => httpClient.post(`/images/${imageId}/focal-point`, focalPoint),
      'Error setting focal point:'
    );
  }
}

const imagesService = new ImagesService();
export default imagesService;
